/**
 * @module @eoln/resilience/retry
 * @description Retry manager with exponential backoff and jitter
 */

import { RetryExhaustedError } from "./errors.ts";

export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxAttempts: number;
  /** Initial delay in milliseconds */
  initialDelayMs: number;
  /** Maximum delay in milliseconds */
  maxDelayMs: number;
  /** Backoff multiplier for exponential backoff */
  backoffMultiplier: number;
  /** Add random jitter to delays */
  jitter: boolean;
  /** Optional name for logging */
  name?: string;
}

export interface RetryMetrics {
  totalAttempts: number;
  totalSuccesses: number;
  totalFailures: number;
  lastAttemptTime: number | null;
}

/**
 * Determines if an error should trigger a retry
 */
export type RetryPredicate = (error: unknown) => boolean;

/**
 * Retry manager that handles transient failures with exponential backoff
 * NO CACHING - all attempts execute fresh
 */
export class RetryManager {
  private readonly config: RetryConfig;
  private totalAttempts = 0;
  private totalSuccesses = 0;
  private totalFailures = 0;
  private lastAttemptTime: number | null = null;

  constructor(config: RetryConfig) {
    this.config = config;
  }

  /**
   * Execute a function with retry logic
   * NO CACHING - each retry executes the function fresh
   */
  async execute<T>(fn: () => Promise<T>, isRetryable?: RetryPredicate): Promise<T> {
    let lastError: unknown;
    const retryPredicate = isRetryable || this.defaultRetryPredicate;

    for (let attempt = 1; attempt <= this.config.maxAttempts; attempt++) {
      this.totalAttempts++;
      this.lastAttemptTime = Date.now();

      try {
        // Always execute fresh - NO CACHING
        const result = await fn();
        this.totalSuccesses++;

        if (this.config.name && attempt > 1) {
          console.info(`${this.config.name}: Success after ${attempt} attempts`);
        }

        return result;
      } catch (error) {
        lastError = error;

        const shouldRetry = retryPredicate(error);
        const isLastAttempt = attempt === this.config.maxAttempts;

        if (!shouldRetry) {
          this.totalFailures++;

          if (this.config.name) {
            console.warn(`${this.config.name}: Non-retryable error on attempt ${attempt}`);
          }

          throw error;
        }

        if (isLastAttempt) {
          this.totalFailures++;

          if (this.config.name) {
            console.error(`${this.config.name}: All ${this.config.maxAttempts} retry attempts exhausted`);
          }

          throw new RetryExhaustedError(this.config.maxAttempts, error instanceof Error ? error : undefined);
        }

        // Calculate and apply delay before next attempt
        const delayMs = this.calculateDelay(attempt);

        if (this.config.name) {
          console.info(`${this.config.name}: Attempt ${attempt} failed, retrying in ${delayMs}ms`);
        }

        await this.delay(delayMs);
      }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError;
  }

  /**
   * Default retry predicate - retries network and timeout errors
   */
  private defaultRetryPredicate(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      // Network and connectivity errors
      if (
        message.includes("network") ||
        message.includes("timeout") ||
        message.includes("econnrefused") ||
        message.includes("enotfound") ||
        message.includes("econnreset") ||
        message.includes("etimedout") ||
        message.includes("socket hang up")
      ) {
        return true;
      }

      // HTTP 5xx errors (server errors are often transient)
      if ("status" in error) {
        const status = (error as { status: unknown }).status;
        if (typeof status === "number" && status >= 500 && status < 600) {
          return true;
        }
      }

      // Rate limiting (HTTP 429)
      if ("status" in error) {
        const status = (error as { status: unknown }).status;
        if (status === 429) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Calculate delay for next retry attempt
   */
  private calculateDelay(attempt: number): number {
    // Exponential backoff
    let delayMs = Math.min(
      this.config.initialDelayMs * this.config.backoffMultiplier ** (attempt - 1),
      this.config.maxDelayMs,
    );

    // Add jitter if configured
    if (this.config.jitter) {
      // Add 0-50% random jitter
      delayMs *= 1 + Math.random() * 0.5;
    }

    return Math.floor(delayMs);
  }

  /**
   * Delay execution for specified milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get retry metrics
   */
  getMetrics(): RetryMetrics {
    return {
      totalAttempts: this.totalAttempts,
      totalSuccesses: this.totalSuccesses,
      totalFailures: this.totalFailures,
      lastAttemptTime: this.lastAttemptTime,
    };
  }

  /**
   * Create a retry predicate for specific HTTP status codes
   */
  static createHttpStatusPredicate(statuses: number[]): RetryPredicate {
    return (error: unknown): boolean => {
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as { status: unknown }).status;
        return typeof status === "number" && statuses.includes(status);
      }
      return false;
    };
  }

  /**
   * Create a retry predicate that combines multiple predicates
   */
  static combinePredicate(...predicates: RetryPredicate[]): RetryPredicate {
    return (error: unknown): boolean => {
      return predicates.some((predicate) => predicate(error));
    };
  }

  /**
   * Create a retry predicate for specific error messages
   */
  static createMessagePredicate(patterns: (string | RegExp)[]): RetryPredicate {
    return (error: unknown): boolean => {
      if (error instanceof Error) {
        const message = error.message;
        return patterns.some((pattern) => {
          if (typeof pattern === "string") {
            return message.includes(pattern);
          }
          return pattern.test(message);
        });
      }
      return false;
    };
  }
}
